import type {
  CreateGoalRequest,
  CreateGoalResponse,
  GoalPlatformStateResponse,
  SetGoalPlatformRequest,
  SetGoalPlatformResponse,
  SnapshotInvalidation,
} from "@repo/shared/types";
import { del, get, patch, post } from "../_client";

const BASE = "/internal/flow-instances";

// --- Goals ---

export function getGoals(instanceId: string) {
  return get<any[]>(`${BASE}/${instanceId}/goals`);
}

export function getGoal(goalId: string) {
  return get<any>(`${BASE}/goals/${goalId}`);
}

export function createGoal(instanceId: string, data: CreateGoalRequest) {
  return post<CreateGoalResponse>(`${BASE}/${instanceId}/goals`, data satisfies CreateGoalRequest);
}

export function updateGoal(goalId: string, data: Record<string, unknown>) {
  return patch<any & { refreshHints?: SnapshotInvalidation }>(`${BASE}/goals/${goalId}`, data);
}

export function deleteGoal(goalId: string) {
  return del(`${BASE}/goals/${goalId}`);
}

// --- Platforms (QP-195) ---

export function getGoalPlatforms(goalId: string, goalType?: string) {
  const params = goalType ? `?goalType=${encodeURIComponent(goalType)}` : "";
  return get<GoalPlatformStateResponse>(`${BASE}/goals/${goalId}/platforms${params}`);
}

export function setGoalPlatform(goalId: string, platformId: string, lang?: string) {
  return post<SetGoalPlatformResponse>(`${BASE}/goals/${goalId}/platform`, {
    platformId,
    lang,
  } satisfies SetGoalPlatformRequest);
}

// --- Custom Products ---

export function addCustomProduct(
  goalId: string,
  data: {
    productId: string;
    amount?: number;
    units?: number;
    weight: number;
  },
) {
  return post<any & { refreshHints?: SnapshotInvalidation }>(
    `${BASE}/goals/${goalId}/products`,
    data,
  );
}

export function updateCustomProduct(
  productId: string,
  data: {
    amount?: number;
    units?: number;
    weight?: number;
    order?: number;
  },
) {
  return patch<any & { refreshHints?: SnapshotInvalidation }>(
    `${BASE}/goals/products/${productId}`,
    data,
  );
}

export function deleteCustomProduct(productId: string) {
  return del<{ success: boolean; goalId: string; refreshHints?: SnapshotInvalidation }>(
    `${BASE}/goals/products/${productId}`,
  );
}

// --- Reference Data ---

export function getAvailableProducts(instanceId: string) {
  return get<any[]>(`${BASE}/${instanceId}/available-products`);
}

export function getProductsWithSuitability(goalId: string) {
  return get<any[]>(`${BASE}/goals/${goalId}/products-suitability`);
}

export function getRiskTimeHorizons(riskModelId: string) {
  return get<any[]>(`${BASE}/reference/risk-time-horizons/${riskModelId}`);
}

export function getRiskLevels(riskModelId: string) {
  return get<any[]>(`${BASE}/reference/risk-levels/${riskModelId}`);
}

export function getReferencePortfolios(collectionId: string) {
  return get<any[]>(`${BASE}/reference/portfolios/${collectionId}`);
}

export function getSuitabilityContext(goalId: string) {
  return get<any>(`${BASE}/goals/${goalId}/suitability-context`);
}

export function getRiskModelOptions(goalId: string) {
  return get<any>(`${BASE}/goals/${goalId}/risk-model-options`);
}
